import React from 'react';
import Modal from '@tencent/dui/lib/components/Modal';
import Checkbox from '@tencent/dui/lib/components/Checkbox';
import Select from '@tencent/dui/lib/components/Select';
import i18n from '@i18next/i18n';
import { IReactionDisposer } from 'mobx/lib/internal';
import { reaction } from 'mobx';
import { IGridLineManager } from '@src/slide/application/service/grid-line-manager/index.interface';
import styles from './index.less';
import Divider from '@tencent/dui/lib/components/Divider';
import { NumberInput } from '@tencent/wb-ext-form';
import { IGuidesManager } from '@src/slide/application/service/guides-manager/index.interface';

interface IGridLineDialogProps {
    gridLineManager: IGridLineManager;
    guidesManager: IGuidesManager;
}

const CUSTOM_SPACE = 'custom_space';
export const DEFAULT_CUSTOM_SPACE = 0.5;

// 网格间距设置值， 1/8,1/6 .. 4cm,5cm
export const ONE_EIGHT = 0.125;
export const ONE_SIXTH = 0.166;
export const ONE_FIFTH = 0.2;
export const ONE_FOURTH = 0.25;
export const ONE_THIRD = 0.333;
export const ONE_SECOND = 0.5;
export const ONE_CM = 1;
export const TWO_CM = 2;
export const THREE_CM = 3;
export const FOUR_CM = 4;
export const FIVE_CM = 5;

export const gridSpaceList = [
    {
        title: i18n.t('每厘米8个网格'),
        value: ONE_EIGHT,
    },
    {
        title: i18n.t('每厘米6个网格'),
        value: ONE_SIXTH,
    },
    {
        title: i18n.t('每厘米5个网格'),
        value: ONE_FIFTH,
    },
    {
        title: i18n.t('每厘米4个网格'),
        value: ONE_FOURTH,
    },
    {
        title: i18n.t('每厘米3个网格'),
        value: ONE_THIRD,
    },
    {
        title: i18n.t('每厘米2个网格'),
        value: ONE_SECOND,
    },
    {
        title: `1${i18n.t('厘米')}`,
        value: ONE_CM,
    },
    {
        title: `2${i18n.t('厘米')}`,
        value: TWO_CM,
    },
    {
        title: `3${i18n.t('厘米')}`,
        value: THREE_CM,
    },
    {
        title: `4${i18n.t('厘米')}`,
        value: FOUR_CM,
    },
    {
        title: `5${i18n.t('厘米')}`,
        value: FIVE_CM,
    },
    {
        title: i18n.t('自定义'),
        value: CUSTOM_SPACE,
    },
];

interface IGridLineDialogState {
    isCustom: boolean;
    gridSpace: number | typeof CUSTOM_SPACE;
    customGridSpace: number;
    displayGridLine: boolean;
    isGridAlign: boolean;
    displayGuides: boolean;
}

/**
 * 网格设置面板
 */
export default class GridLineDialog extends React.Component<IGridLineDialogProps, IGridLineDialogState> {
    reaction: IReactionDisposer;
    gridLineManager: IGridLineManager;
    guidesManager: IGuidesManager;

    constructor(props: IGridLineDialogProps) {
        super(props);
        const { gridLineManager, guidesManager } = props;
        const { gridSpace, displayGridLine, isGridAlign } = gridLineManager;
        const { displayGuides } = guidesManager;
        this.gridLineManager = gridLineManager;
        this.guidesManager = guidesManager;

        const isCustom = this.isCustomGridSpace(gridSpace);

        this.state = {
            isCustom,
            gridSpace: isCustom ? CUSTOM_SPACE : gridSpace,
            customGridSpace: isCustom ? gridSpace : DEFAULT_CUSTOM_SPACE,
            displayGridLine,
            isGridAlign,
            displayGuides,
        };

        this.reaction = reaction(() => [
            this.gridLineManager.displayGridLineDialog,
            this.gridLineManager.isGridAlign,
            this.gridLineManager.displayGridLine,
            this.guidesManager.displayGuides,
        ], (props) => {
            const [displayGridLineDialog, isGridAlign, displayGridLine, displayGuides] = props;
            this.setState({
                isGridAlign,
                displayGridLine,
                displayGuides,
            });
        });
    }

    public componentWillUnmount() {
        this.reaction?.();
    }

    public render() {
        return <div
            onDoubleClick={(e) => {
                e.stopPropagation();
            }}
        ><Modal
                draggable={true}
                maskClosable={false}
                title={i18n.t('网格和参考线')}
                visible={this.gridLineManager.displayGridLineDialog}
                okText={i18n.t('确定')}
                cancelText={i18n.t('取消')}
                onCancel={this.hideModal}
                onOk={this.handleOk}
                destroyOnClose={true}
                onClickMask={(e) => {
                    e.stopPropagation();
                }}
            >
                <div
                    className={styles.modelInner}>
                    <div className={styles.subTitle}>{`${i18n.t('对齐')}`}</div>
                    <div className={styles.modelContentLine}>
                        <Checkbox
                            className={styles.checkFontStyle}
                            onChange={(value) => {
                                this.setState({ isGridAlign: value });
                            }}
                            checked={this.state.isGridAlign}>
                            {i18n.t('对象与网格对齐')}
                        </Checkbox>
                    </div>
                    <div className={styles.subTitle}>{`${i18n.t('网格设置')}`}</div>
                    <div className={styles.contentLineSpace} />
                    <div className={styles.modelContentLine}>
                        <div className={styles.gridSpaceLabel}>
                            {`${i18n.t('间距')}${i18n.t('：')}`}
                        </div>
                        <div>
                            <Select
                                className={styles.selectStyle}
                                onChange={(value) => {
                                    const isCustom = value === CUSTOM_SPACE;
                                    const gridSpace = isCustom ? CUSTOM_SPACE : Number(value);
                                    this.setState({
                                        isCustom,
                                        gridSpace,
                                        customGridSpace: DEFAULT_CUSTOM_SPACE,
                                    });
                                }}
                                value={this.state.gridSpace} >
                                {
                                    gridSpaceList.map(item => <Select.Option
                                        className={styles.optionStyle}
                                        key={item.value}
                                        value={item.value}
                                    >
                                        {item.title}
                                    </Select.Option>)
                                }
                            </Select>
                        </div>
                        {
                            this.state.isCustom && <div className={styles.numberInput}>
                                <NumberInput
                                    min={ONE_EIGHT}
                                    max={FIVE_CM}
                                    step={0.1}
                                    toFixed={2}
                                    allowDecimal
                                    defaultValue={0.5}
                                    onChange={this.handleCustomSpaceChange}
                                    value={String(this.state.customGridSpace)}
                                    style={{ height: 28, width: 65 }} />
                            </div>
                        }
                    </div>
                    <div className={styles.contentLineSpace} />
                    <div className={styles.modelContentLine}>
                        <Checkbox
                            className={styles.checkFontStyle}
                            onChange={value => this.setState({ displayGridLine: value })}
                            checked={this.state.displayGridLine}>
                            {i18n.t('屏幕上显示网格')}
                        </Checkbox>
                    </div>
                    <Divider style={{ marginBottom: '25px' }} />
                    <div className={styles.subTitle}>{`${i18n.t('参考线设置')}`}</div>
                    <div className={styles.modelContentLine}>
                        <Checkbox
                            className={styles.checkFontStyle}
                            onChange={value => this.setState({ displayGuides: value })}
                            checked={this.state.displayGuides}>
                            {i18n.t('屏幕上显示绘图参考线')}
                        </Checkbox>
                    </div>
                </div>
            </Modal></div>;
    }

    /**
     * 关闭modal，还原间距属性
     */
    private hideModal = (): void => {
        this.gridLineManager.displayGridLineDialog = false;
        this.resetState();
    };

    private updateComponent = (): void => {
        this.forceUpdate();
    };

    /**
     * 是否是自定义间距
     * @param gridSpace
     */
    // eslint-disable-next-line max-len
    private isCustomGridSpace = (gridSpace: number): boolean => gridSpaceList.findIndex(item => item.value === gridSpace) === -1;

    /**
     * 修改网格间距
     */
    private handleOk = (): void => {
        const gridSpace = this.state.isCustom ? this.state.customGridSpace : this.state.gridSpace;
        this.gridLineManager.setGridSpace(Number(gridSpace));
        this.gridLineManager.displayGridLineDialog = false;
        this.gridLineManager.setIsGridAlign(this.state.isGridAlign);
        this.gridLineManager.setDisplayGridLine(this.state.displayGridLine);
        this.guidesManager.setDisplayGuides(this.state.displayGuides);
    };

    /**
     * 处理自定义间距修改·
     * @param value
     */
    private handleCustomSpaceChange = (value: string): void => {
        this.setState({
            customGridSpace: Number(value),
        });
    };

    /**
     * 重置状态
     */
    private resetState = (): void => {
        const { gridSpace, displayGridLine, isGridAlign } = this.gridLineManager;
        const { displayGuides } = this.guidesManager;
        const isCustom = this.isCustomGridSpace(gridSpace);

        this.setState({
            gridSpace: isCustom ? CUSTOM_SPACE : gridSpace,
            isCustom,
            displayGridLine,
            isGridAlign,
            displayGuides,
        });
    };
}
